import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, Polyline, Polygon, DrawingManager } from '@react-google-maps/api';
import MapErrorBanner from '../components/MapErrorBanner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import { useAuth } from '../context/AuthContext';
import { FieldDispatchPanel } from './FieldCommandCenter';
import {
  Target, RefreshCw, Plus, MapPin, Eye, Navigation, AlertTriangle,
  Crosshair, Shield, Users, Trash2, Save, FolderOpen, Pencil,
  Route, Square, Circle, Pentagon, MousePointer, Palette, X, Check,
  FileText, Download
} from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const LIBRARIES = ['places', 'drawing'];

// Tactical marker types with icons and colors
const MARKER_TYPES = {
  SUBJECT_LOCATION: { color: '#EF4444', label: 'Subject Location', icon: Target },
  SURVEILLANCE_POST: { color: '#8B5CF6', label: 'Surveillance Post', icon: Eye },
  ENTRY_POINT: { color: '#10B981', label: 'Entry Point', icon: Navigation },
  EXIT_POINT: { color: '#F59E0B', label: 'Exit Point', icon: Navigation },
  TEAM_POSITION: { color: '#3B82F6', label: 'Team Position', icon: Users },
  HAZARD: { color: '#DC2626', label: 'Hazard', icon: AlertTriangle },
  PERIMETER: { color: '#6366F1', label: 'Perimeter Point', icon: Shield },
  CUSTOM: { color: '#94A3B8', label: 'Custom Pin', icon: MapPin }
};

// Shape types for drawing
const SHAPE_TYPES = {
  ZONE: { color: '#3B82F6', label: 'Tactical Zone', icon: Square, fillOpacity: 0.2 },
  ROUTE: { color: '#10B981', label: 'Movement Route', icon: Route, fillOpacity: 0 },
  AREA: { color: '#F59E0B', label: 'Search Area', icon: Pentagon, fillOpacity: 0.15 },
  PERIMETER_LINE: { color: '#6366F1', label: 'Perimeter Line', icon: Circle, fillOpacity: 0 },
  SURVEILLANCE_AREA: { color: '#8B5CF6', label: 'Surveillance Zone', icon: Eye, fillOpacity: 0.2 },
  EXCLUSION_ZONE: { color: '#DC2626', label: 'Exclusion Zone', icon: AlertTriangle, fillOpacity: 0.3 }
};

// Color palette for customization
const COLOR_PALETTE = [
  '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#6366F1', '#8B5CF6', '#EC4899', '#DC2626', '#059669', '#7C3AED'
];

const mapContainerStyle = { width: '100%', height: '100%' };
const defaultCenter = { lat: 32.7767, lng: -96.7970 };
const satelliteMapOptions = {
  mapTypeId: 'hybrid',
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: true,
  streetViewControl: true,
  fullscreenControl: true,
  tilt: 0
};

// Iter132 — Field Map On/Off Duty toggle.  Reuses the same backend as
// StatusPill (/api/investigators/me/status) but surfaces a prominent
// button right on the Field Map page.  On ON-DUTY it starts a
// `watchPosition` push loop so admin dispatch sees the live pin until
// the user flips back to OFF-DUTY.  Admin-triggered status changes land
// in the same investigator_tracking row and backend notifies dispatch.
function DutyToggleButton() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('nexus_token') : null;
  const [status, setStatus] = React.useState('OFF_DUTY');
  const [busy, setBusy] = React.useState(false);
  const watchRef = React.useRef(null);

  const pushLoc = React.useCallback(async (lat, lng) => {
    if (!token) return;
    try {
      await fetch(`${BACKEND_URL}/api/investigators/location?latitude=${lat}&longitude=${lng}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
    } catch {}
  }, [token]);

  const startWatch = React.useCallback(() => {
    if (!('geolocation' in navigator) || watchRef.current) return;
    watchRef.current = navigator.geolocation.watchPosition(
      (p) => pushLoc(p.coords.latitude, p.coords.longitude),
      () => {},
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 30000 },
    );
  }, [pushLoc]);
  const stopWatch = React.useCallback(() => {
    if (watchRef.current && 'geolocation' in navigator) {
      navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/investigators/me/status`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) {
          const d = await r.json();
          setStatus(d.status || 'OFF_DUTY');
          if ((d.status || 'OFF_DUTY') !== 'OFF_DUTY') startWatch();
        }
      } catch {}
    })();
    return () => stopWatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const toggle = async () => {
    if (!token || busy) return;
    const next = status === 'OFF_DUTY' ? 'AVAILABLE' : 'OFF_DUTY';
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/investigators/me/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: next }),
      });
      if (!r.ok) { toast({ title: 'Status change failed', variant: 'destructive' }); return; }
      setStatus(next);
      if (next === 'OFF_DUTY') { stopWatch(); toast({ title: 'Off Duty', description: 'GPS stopped' }); }
      else {
        startWatch();
        if ('geolocation' in navigator) {
          navigator.geolocation.getCurrentPosition(
            (p) => pushLoc(p.coords.latitude, p.coords.longitude),
            () => {},
          );
        }
        toast({ title: 'On Duty', description: 'Live GPS enabled' });
      }
    } finally { setBusy(false); }
  };

  const isOn = status !== 'OFF_DUTY';
  return (
    <Button
      onClick={toggle}
      disabled={busy}
      data-testid="field-map-duty-toggle"
      className={isOn
        ? 'bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold'
        : 'bg-slate-600 hover:bg-slate-500 text-white font-semibold'}>
      <Circle className={`w-3 h-3 mr-2 ${isOn ? 'fill-slate-900' : 'fill-white animate-pulse'}`} />
      {isOn ? 'Off Duty' : 'Go On Duty'}
    </Button>
  );
}

const CaseOperationsMap = () => {
  const { user } = useAuth();
  const isAdminUser = user?.role === 'main_hub' || user?.role === 'department_admin' || user?.role === 'super_admin';
  const token = localStorage.getItem('nexus_token');
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState('');
  const [operationMarkers, setOperationMarkers] = useState([]);
  const [drawingShapes, setDrawingShapes] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [selectedShape, setSelectedShape] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  
  // Drawing mode state
  const [drawingMode, setDrawingMode] = useState(null); // 'marker', 'polygon', 'polyline', null
  const [activeDrawTool, setActiveDrawTool] = useState(null); // ZONE, ROUTE, etc
  
  // Dialogs
  const [showAddMarker, setShowAddMarker] = useState(false);
  const [showAddShape, setShowAddShape] = useState(false);
  const [showEditMarker, setShowEditMarker] = useState(false);
  const [showEditShape, setShowEditShape] = useState(false);
  
  // New marker/shape data
  const [clickedPosition, setClickedPosition] = useState(null);
  const [newMarker, setNewMarker] = useState({ marker_type: 'SURVEILLANCE_POST', label: '', notes: '', color: null });
  const [drawnCoordinates, setDrawnCoordinates] = useState([]);
  const [newShape, setNewShape] = useState({ shape_type: 'ZONE', label: '', notes: '', color: '#3B82F6' });
  
  // Edit states
  const [editingMarker, setEditingMarker] = useState(null);
  const [editingShape, setEditingShape] = useState(null);
  
  const mapRef = useRef(null);
  const addrSearchRef = useRef(null);
  const drawingManagerRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES
  });

  // Fetch cases
  const fetchCases = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Iter104 — Previously filtered to only ACTIVE/SURVEILLANCE/ASSIGNED
        // etc., which made the dropdown empty in production for cases with
        // other statuses. Now show ALL cases and let the user pick.
        // Filter out only fully-closed/archived cases so the dropdown stays
        // focused on actionable investigations.
        const workable = data.filter(c =>
          !['CLOSED', 'ARCHIVED', 'CANCELLED', 'REJECTED'].includes((c.status || '').toUpperCase())
        );
        setCases(workable);
      } else {
        toast({ title: 'Cases unavailable', description: `HTTP ${res.status}`, variant: 'destructive' });
      }
    } catch (e) {
      console.error('Fetch cases error:', e);
      toast({ title: 'Network error', description: e.message, variant: 'destructive' });
    }
  }, [token]);

  // Fetch operation markers
  const fetchOperationMarkers = useCallback(async () => {
    if (!selectedCase) { setOperationMarkers([]); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${selectedCase}/operations-markers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOperationMarkers(data);
        if (data.length > 0) {
          setMapCenter({ lat: data[0].latitude, lng: data[0].longitude });
        }
      }
    } catch (e) {
      console.error('Fetch markers error:', e);
    } finally {
      setLoading(false);
    }
  }, [token, selectedCase]);

  // Fetch drawing shapes
  const fetchDrawingShapes = useCallback(async () => {
    if (!selectedCase) { setDrawingShapes([]); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${selectedCase}/drawing-shapes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setDrawingShapes(await res.json());
      }
    } catch (e) {
      console.error('Fetch shapes error:', e);
    }
  }, [token, selectedCase]);

  useEffect(() => { fetchCases(); }, [fetchCases]);
  useEffect(() => { fetchOperationMarkers(); fetchDrawingShapes(); }, [fetchOperationMarkers, fetchDrawingShapes]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    // Wire Google Places Autocomplete for the address search bar
    if (addrSearchRef.current && window.google?.maps?.places?.Autocomplete) {
      const ac = new window.google.maps.places.Autocomplete(addrSearchRef.current, {
        types: ['geocode'],
        fields: ['formatted_address', 'geometry'],
      });
      ac.addListener('place_changed', () => {
        const p = ac.getPlace();
        if (p?.geometry?.location) {
          const lat = p.geometry.location.lat();
          const lng = p.geometry.location.lng();
          map.panTo({ lat, lng });
          map.setZoom(18);
        }
      });
    }
  }, []);

  // Handle map click for markers
  const handleMapClick = (e) => {
    if (drawingMode || !selectedCase) return;
    setClickedPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    setShowAddMarker(true);
  };

  // Create marker
  const handleAddMarker = async () => {
    if (!clickedPosition || !selectedCase) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${selectedCase}/operations-markers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...newMarker,
          latitude: clickedPosition.lat,
          longitude: clickedPosition.lng
        })
      });
      if (res.ok) {
        toast({ title: 'Marker Added', description: 'Operation marker has been placed' });
        setShowAddMarker(false);
        setClickedPosition(null);
        setNewMarker({ marker_type: 'SURVEILLANCE_POST', label: '', notes: '', color: null });
        fetchOperationMarkers();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  // Update marker
  const handleUpdateMarker = async () => {
    if (!editingMarker) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/operations-markers/${editingMarker.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          label: editingMarker.label,
          notes: editingMarker.notes,
          marker_type: editingMarker.marker_type,
          color: editingMarker.color
        })
      });
      if (res.ok) {
        toast({ title: 'Marker Updated' });
        setShowEditMarker(false);
        setEditingMarker(null);
        setSelectedMarker(null);
        fetchOperationMarkers();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  // Delete marker
  const handleDeleteMarker = async (markerId) => {
    if (!window.confirm('Delete this operation marker?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/operations-markers/${markerId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Marker Deleted' });
      fetchOperationMarkers();
      setSelectedMarker(null);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  // Handle drawing complete
  const onPolygonComplete = (polygon) => {
    const path = polygon.getPath();
    const coords = [];
    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i);
      coords.push({ lat: point.lat(), lng: point.lng() });
    }
    polygon.setMap(null); // Remove the temporary polygon
    setDrawnCoordinates(coords);
    setNewShape({ ...newShape, shape_type: activeDrawTool || 'ZONE' });
    setShowAddShape(true);
    setDrawingMode(null);
    setActiveDrawTool(null);
  };

  const onPolylineComplete = (polyline) => {
    const path = polyline.getPath();
    const coords = [];
    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i);
      coords.push({ lat: point.lat(), lng: point.lng() });
    }
    polyline.setMap(null);
    setDrawnCoordinates(coords);
    setNewShape({ ...newShape, shape_type: activeDrawTool || 'ROUTE' });
    setShowAddShape(true);
    setDrawingMode(null);
    setActiveDrawTool(null);
  };

  // Create shape
  const handleAddShape = async () => {
    if (drawnCoordinates.length < 2 || !selectedCase) return;
    try {
      const shapeConfig = SHAPE_TYPES[newShape.shape_type];
      const res = await fetch(`${BACKEND_URL}/api/cases/${selectedCase}/drawing-shapes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          shape_type: newShape.shape_type,
          coordinates: drawnCoordinates,
          label: newShape.label,
          notes: newShape.notes,
          color: newShape.color || shapeConfig?.color || '#3B82F6',
          stroke_weight: 3,
          fill_opacity: shapeConfig?.fillOpacity || 0.2
        })
      });
      if (res.ok) {
        toast({ title: 'Shape Created', description: `${SHAPE_TYPES[newShape.shape_type]?.label || 'Shape'} has been added` });
        setShowAddShape(false);
        setDrawnCoordinates([]);
        setNewShape({ shape_type: 'ZONE', label: '', notes: '', color: '#3B82F6' });
        fetchDrawingShapes();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  // Update shape
  const handleUpdateShape = async () => {
    if (!editingShape) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/drawing-shapes/${editingShape.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          label: editingShape.label,
          notes: editingShape.notes,
          color: editingShape.color
        })
      });
      if (res.ok) {
        toast({ title: 'Shape Updated' });
        setShowEditShape(false);
        setEditingShape(null);
        setSelectedShape(null);
        fetchDrawingShapes();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  // Delete shape
  const handleDeleteShape = async (shapeId) => {
    if (!window.confirm('Delete this shape?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/drawing-shapes/${shapeId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Shape Deleted' });
      fetchDrawingShapes();
      setSelectedShape(null);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  // Start drawing tool
  const startDrawing = (toolType) => {
    if (!selectedCase) {
      toast({ title: 'Select a Case', description: 'Please select a case first', variant: 'destructive' });
      return;
    }
    setActiveDrawTool(toolType);
    // ROUTE and PERIMETER_LINE use polyline, others use polygon
    if (toolType === 'ROUTE' || toolType === 'PERIMETER_LINE') {
      setDrawingMode('polyline');
    } else {
      setDrawingMode('polygon');
    }
  };

  const cancelDrawing = () => {
    setDrawingMode(null);
    setActiveDrawTool(null);
    setDrawnCoordinates([]);
  };

  // Export Operational Plan PDF
  const [exporting, setExporting] = useState(false);
  
  const handleExportPlan = async () => {
    if (!selectedCase) {
      toast({ title: 'Select a Case', description: 'Please select a case to export', variant: 'destructive' });
      return;
    }
    setExporting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${selectedCase}/operational-plan/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `OpPlan_${selectedCaseData?.case_number || selectedCase}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast({ title: 'Export Complete', description: 'Operational Plan PDF downloaded' });
      } else {
        toast({ title: 'Export Failed', description: 'Failed to generate PDF', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const getMarkerIcon = (marker) => {
    const config = MARKER_TYPES[marker.marker_type] || MARKER_TYPES.CUSTOM;
    const color = marker.color || config.color;
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 14,
      fillColor: color,
      fillOpacity: 0.9,
      strokeColor: '#FFFFFF',
      strokeWeight: 3
    };
  };

  // Perimeter points for legacy polyline
  const perimeterPoints = operationMarkers
    .filter(m => m.marker_type === 'PERIMETER')
    .map(m => ({ lat: m.latitude, lng: m.longitude }));

  if (loadError) {
    return (
      <div className="max-w-3xl mx-auto p-6" data-testid="ops-map-load-error">
        <MapErrorBanner />
        <div className="mt-4 text-slate-300 text-sm">
          <p className="mb-2 font-semibold text-white">Failed to initialize Google Maps.</p>
          <p className="text-slate-300">
            Ask your Main Hub admin to enable <span className="font-mono">Maps JavaScript API</span> and
            enable billing in your Google Cloud project associated with the key.
          </p>
        </div>
      </div>
    );
  }

  const selectedCaseData = cases.find(c => c.id === selectedCase);

  return (
    <div className="space-y-6" data-testid="case-operations-map-page">
      {/* Iter104 — surface runtime maps errors (billing, API not activated, etc.) */}
      <MapErrorBanner />
      {/* Iter132 — Field Map merge: admins see the live Dispatch Panel
          (ex-"Field Ops Command") above the tactical satellite tools. */}
      {isAdminUser && (
        <div className="rounded-lg border border-red-500/30 bg-[#131A2A] p-3" data-testid="field-map-dispatch">
          <FieldDispatchPanel embedded />
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#F8FAFC] flex items-center gap-3">
            <div className="w-10 h-10 bg-[#DC2626] rounded-lg flex items-center justify-center">
              <Crosshair className="w-5 h-5 text-white" />
            </div>
            Field Map
          </h1>
          <p className="text-[#94A3B8] mt-1">Tactical field planning · Live dispatch · Investigator telemetry</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedCase} onValueChange={setSelectedCase}>
            <SelectTrigger className="w-[260px] bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" data-testid="case-select">
              <SelectValue placeholder="Select active case" />
            </SelectTrigger>
            <SelectContent className="bg-[#131A2A] border-[#334155] max-h-[300px]">
              {cases.length === 0 ? (
                <SelectItem value="none" disabled className="text-[#94A3B8]">No active cases</SelectItem>
              ) : (
                cases.map(c => (
                  <SelectItem key={c.id} value={c.id} className="text-[#F8FAFC]">
                    {c.case_number} - {c.title?.substring(0, 25)}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          <Button variant="outline" className="border-[#334155] text-[#94A3B8] hover:bg-[#182136]" onClick={() => { fetchOperationMarkers(); fetchDrawingShapes(); }}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <DutyToggleButton />
          <Button 
            className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" 
            onClick={handleExportPlan}
            disabled={!selectedCase || exporting}
            data-testid="export-plan-btn"
          >
            {exporting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            Export Plan
          </Button>
        </div>
      </div>

      {/* Case Info Bar */}
      {selectedCaseData && (
        <Card className="bg-[#131A2A] border-[#334155]">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <Badge className="bg-[#DC2626]/20 text-[#DC2626] border border-[#DC2626]/30 px-3">
                  <FolderOpen className="w-3 h-3 mr-1" />
                  {selectedCaseData.case_number}
                </Badge>
                <span className="text-[#F8FAFC] font-medium">{selectedCaseData.title}</span>
                <Badge className="bg-[#182136] text-[#94A3B8] border border-[#334155]">
                  {selectedCaseData.case_type?.replace(/_/g, ' ')}
                </Badge>
              </div>
              <p className="text-xs text-[#94A3B8]">
                {operationMarkers.length} markers • {drawingShapes.length} shapes
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Drawing Tools Bar */}
      <Card className="bg-[#131A2A] border-[#334155]">
        <CardContent className="py-3 px-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-[#94A3B8] uppercase tracking-wider mr-2 font-bold">Drawing Tools:</span>
            
            {/* Marker tool */}
            <Button size="sm" variant={drawingMode === null && !activeDrawTool ? 'default' : 'outline'}
              className={drawingMode === null && !activeDrawTool ? 'bg-[#1D4ED8]' : 'border-[#334155] text-[#94A3B8]'}
              onClick={cancelDrawing} data-testid="select-tool">
              <MousePointer className="w-4 h-4 mr-1" /> Select
            </Button>

            {/* Shape tools */}
            {Object.entries(SHAPE_TYPES).map(([key, config]) => {
              const IconComponent = config.icon;
              const isActive = activeDrawTool === key;
              return (
                <Button key={key} size="sm" variant={isActive ? 'default' : 'outline'}
                  className={isActive ? 'bg-[#1D4ED8]' : 'border-[#334155] text-[#94A3B8] hover:bg-[#182136]'}
                  onClick={() => startDrawing(key)}
                  data-testid={`draw-${key.toLowerCase()}`}>
                  <IconComponent className="w-4 h-4 mr-1" style={{ color: isActive ? '#fff' : config.color }} />
                  {config.label}
                </Button>
              );
            })}

            {/* Cancel drawing */}
            {drawingMode && (
              <Button size="sm" variant="outline" className="border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626]/10 ml-2"
                onClick={cancelDrawing} data-testid="cancel-draw">
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            )}
          </div>
          {drawingMode && (
            <p className="text-xs text-[#3B82F6] mt-2">
              {drawingMode === 'polygon' ? 'Click points on the map to draw a zone. Click first point again to complete.' : 
               'Click points on the map to draw a route. Double-click to complete.'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Map */}
        <div className="lg:col-span-3">
          <Card className="bg-[#131A2A] border-[#334155] h-[600px]">
            <CardContent className="p-0 h-full">
              {!isLoaded ? (
                <div className="flex items-center justify-center h-full">
                  <RefreshCw className="w-8 h-8 text-[#3B82F6] animate-spin" />
                </div>
              ) : (
                <div className="relative h-full">
                <div className="absolute top-3 left-3 z-10 bg-[#0B1220]/95 backdrop-blur border border-slate-700 rounded p-2 flex items-center gap-2 shadow-lg" data-testid="ops-map-address-search-wrap" style={{ maxWidth: 'calc(100% - 24px)' }}>
                  <MapPin size={14} className="text-amber-300 flex-shrink-0" />
                  <input
                    ref={addrSearchRef}
                    type="text"
                    placeholder="Search a real address…"
                    className="bg-[#0B1220] border border-slate-700 text-white text-xs rounded px-2 py-1.5 w-72 max-w-full focus:outline-none focus:border-amber-400"
                    data-testid="ops-map-address-search"
                  />
                </div>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={17}
                  onLoad={onMapLoad}
                  onClick={handleMapClick}
                  options={satelliteMapOptions}
                >
                  {/* Drawing Manager */}
                  {drawingMode && (
                    <DrawingManager
                      onPolygonComplete={onPolygonComplete}
                      onPolylineComplete={onPolylineComplete}
                      options={{
                        drawingMode: drawingMode === 'polygon' ? window.google.maps.drawing.OverlayType.POLYGON : window.google.maps.drawing.OverlayType.POLYLINE,
                        drawingControl: false,
                        polygonOptions: {
                          fillColor: SHAPE_TYPES[activeDrawTool]?.color || '#3B82F6',
                          fillOpacity: SHAPE_TYPES[activeDrawTool]?.fillOpacity || 0.2,
                          strokeColor: SHAPE_TYPES[activeDrawTool]?.color || '#3B82F6',
                          strokeWeight: 3,
                          editable: true
                        },
                        polylineOptions: {
                          strokeColor: SHAPE_TYPES[activeDrawTool]?.color || '#10B981',
                          strokeWeight: 3,
                          editable: true
                        }
                      }}
                    />
                  )}

                  {/* Render saved shapes */}
                  {drawingShapes.map(shape => {
                    const coords = shape.coordinates?.map(c => ({ lat: c.lat, lng: c.lng })) || [];
                    const isRoute = shape.shape_type === 'ROUTE' || shape.shape_type === 'PERIMETER_LINE';
                    const isSelected = selectedShape?.id === shape.id;
                    
                    if (isRoute) {
                      return (
                        <Polyline
                          key={shape.id}
                          path={coords}
                          options={{
                            strokeColor: shape.color || SHAPE_TYPES[shape.shape_type]?.color || '#3B82F6',
                            strokeWeight: isSelected ? 5 : (shape.stroke_weight || 3),
                            strokeOpacity: 0.9
                          }}
                          onClick={() => setSelectedShape(shape)}
                        />
                      );
                    } else {
                      return (
                        <Polygon
                          key={shape.id}
                          paths={coords}
                          options={{
                            fillColor: shape.color || SHAPE_TYPES[shape.shape_type]?.color || '#3B82F6',
                            fillOpacity: shape.fill_opacity || SHAPE_TYPES[shape.shape_type]?.fillOpacity || 0.2,
                            strokeColor: shape.color || SHAPE_TYPES[shape.shape_type]?.color || '#3B82F6',
                            strokeWeight: isSelected ? 5 : (shape.stroke_weight || 3),
                            strokeOpacity: 0.9
                          }}
                          onClick={() => setSelectedShape(shape)}
                        />
                      );
                    }
                  })}

                  {/* Legacy perimeter outline from perimeter markers */}
                  {perimeterPoints.length >= 3 && (
                    <Polyline
                      path={[...perimeterPoints, perimeterPoints[0]]}
                      options={{ strokeColor: '#6366F1', strokeOpacity: 0.8, strokeWeight: 3 }}
                    />
                  )}

                  {/* Operation markers */}
                  {operationMarkers.map(marker => (
                    <Marker
                      key={marker.id}
                      position={{ lat: marker.latitude, lng: marker.longitude }}
                      onClick={() => setSelectedMarker(marker)}
                      icon={getMarkerIcon(marker)}
                      label={{
                        text: marker.label || '',
                        color: '#FFFFFF',
                        fontSize: '10px',
                        fontWeight: 'bold'
                      }}
                    />
                  ))}

                  {/* Clicked position for new marker */}
                  {clickedPosition && !drawingMode && (
                    <Marker
                      position={clickedPosition}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 10,
                        fillColor: '#FFFFFF',
                        fillOpacity: 0.5,
                        strokeColor: '#3B82F6',
                        strokeWeight: 3
                      }}
                    />
                  )}

                  {/* Selected marker info window */}
                  {selectedMarker && (
                    <InfoWindow
                      position={{ lat: selectedMarker.latitude, lng: selectedMarker.longitude }}
                      onCloseClick={() => setSelectedMarker(null)}
                    >
                      <div className="p-2 min-w-[220px]">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: selectedMarker.color || MARKER_TYPES[selectedMarker.marker_type]?.color }} />
                          <span className="font-bold text-gray-900">{MARKER_TYPES[selectedMarker.marker_type]?.label}</span>
                        </div>
                        {selectedMarker.label && <p className="text-sm text-gray-700 font-medium">{selectedMarker.label}</p>}
                        {selectedMarker.notes && <p className="text-sm text-gray-600 mt-1">{selectedMarker.notes}</p>}
                        <p className="text-xs text-gray-400 mt-2">By: {selectedMarker.created_by_name}</p>
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => { setEditingMarker({...selectedMarker}); setShowEditMarker(true); }}
                            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                          >
                            <Pencil className="w-3 h-3" /> Edit
                          </button>
                          <button
                            onClick={() => handleDeleteMarker(selectedMarker.id)}
                            className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                        </div>
                      </div>
                    </InfoWindow>
                  )}
                </GoogleMap>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Side Panel */}
        <div className="space-y-4">
          {/* Marker Types Legend */}
          <Card className="bg-[#131A2A] border-[#334155]">
            <CardHeader className="pb-2">
              <CardTitle className="text-[#F8FAFC] text-sm flex items-center gap-2">
                <Target className="w-4 h-4 text-[#DC2626]" />
                Marker Types
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {Object.entries(MARKER_TYPES).map(([key, config]) => {
                const IconComponent = config.icon;
                const count = operationMarkers.filter(m => m.marker_type === key).length;
                return (
                  <div key={key} className="flex items-center gap-2 text-xs">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: config.color }}>
                      <IconComponent className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-[#94A3B8] flex-1">{config.label}</span>
                    <span className="text-[#475569]">({count})</span>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Shapes List */}
          <Card className="bg-[#131A2A] border-[#334155]">
            <CardHeader className="pb-2">
              <CardTitle className="text-[#F8FAFC] text-sm flex items-center gap-2">
                <Pentagon className="w-4 h-4 text-[#3B82F6]" />
                Drawn Shapes ({drawingShapes.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[200px] overflow-y-auto">
              {drawingShapes.length === 0 ? (
                <p className="text-[#94A3B8] text-xs text-center py-4">
                  Use drawing tools to add shapes
                </p>
              ) : (
                drawingShapes.map(shape => {
                  const config = SHAPE_TYPES[shape.shape_type];
                  const IconComponent = config?.icon || Square;
                  return (
                    <div
                      key={shape.id}
                      className={`p-2 rounded border cursor-pointer transition-colors ${
                        selectedShape?.id === shape.id
                          ? 'bg-[#1D4ED8]/20 border-[#1D4ED8]'
                          : 'bg-[#0B0F19] border-[#334155] hover:border-[#475569]'
                      }`}
                      onClick={() => setSelectedShape(shape)}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: shape.color || config?.color }}>
                          <IconComponent className="w-3 h-3 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[#F8FAFC] text-xs font-medium truncate">{shape.label || config?.label}</p>
                          {shape.notes && <p className="text-[10px] text-[#94A3B8] truncate">{shape.notes}</p>}
                        </div>
                        <div className="flex gap-1">
                          <button onClick={(e) => { e.stopPropagation(); setEditingShape({...shape}); setShowEditShape(true); }} className="p-1 hover:bg-[#182136] rounded">
                            <Pencil className="w-3 h-3 text-[#94A3B8]" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleDeleteShape(shape.id); }} className="p-1 hover:bg-[#DC2626]/20 rounded">
                            <Trash2 className="w-3 h-3 text-[#DC2626]" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>

          {/* Placed Markers */}
          <Card className="bg-[#131A2A] border-[#334155]">
            <CardHeader className="pb-2">
              <CardTitle className="text-[#F8FAFC] text-sm">
                Placed Markers ({operationMarkers.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[200px] overflow-y-auto nx-inner-scroll-keep" data-testid="ops-markers-list">
              {operationMarkers.length === 0 ? (
                <p className="text-[#94A3B8] text-xs text-center py-4">
                  {selectedCase ? 'Click on map to add markers' : 'Select a case to begin'}
                </p>
              ) : (
                operationMarkers.map(marker => {
                  const config = MARKER_TYPES[marker.marker_type];
                  const IconComponent = config?.icon || Target;
                  return (
                    <div
                      key={marker.id}
                      className={`p-2 rounded border cursor-pointer transition-colors ${
                        selectedMarker?.id === marker.id
                          ? 'bg-[#1D4ED8]/20 border-[#1D4ED8]'
                          : 'bg-[#0B0F19] border-[#334155] hover:border-[#475569]'
                      }`}
                      onClick={() => {
                        setSelectedMarker(marker);
                        if (mapRef.current) {
                          mapRef.current.panTo({ lat: marker.latitude, lng: marker.longitude });
                        }
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: marker.color || config?.color }}>
                          <IconComponent className="w-3 h-3 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[#F8FAFC] text-xs font-medium truncate">{marker.label || config?.label}</p>
                          {marker.notes && <p className="text-[10px] text-[#94A3B8] truncate">{marker.notes}</p>}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>

          {/* Instructions */}
          <Card className="bg-[#0B0F19] border-[#334155]">
            <CardContent className="py-3 px-4">
              <p className="text-[10px] text-[#94A3B8] uppercase tracking-wider mb-2 font-bold">Instructions</p>
              <ul className="text-xs text-[#94A3B8] space-y-1">
                <li>• Select a case to begin planning</li>
                <li>• Click map to place tactical markers</li>
                <li>• Use drawing tools for zones/routes</li>
                <li>• Click shapes to edit or delete</li>
                <li>• All changes save automatically</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Marker Dialog */}
      <Dialog open={showAddMarker} onOpenChange={setShowAddMarker}>
        <DialogContent className="bg-[#131A2A] border-[#334155] text-[#F8FAFC]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#3B82F6]" />
              Add Operation Marker
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-[#94A3B8]">Marker Type</Label>
              <Select value={newMarker.marker_type} onValueChange={v => setNewMarker({...newMarker, marker_type: v})}>
                <SelectTrigger className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#131A2A] border-[#334155]">
                  {Object.entries(MARKER_TYPES).map(([key, config]) => (
                    <SelectItem key={key} value={key} className="text-[#F8FAFC]">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#94A3B8]">Custom Color (optional)</Label>
              <div className="flex gap-2 mt-1">
                {COLOR_PALETTE.map(color => (
                  <button key={color} onClick={() => setNewMarker({...newMarker, color})}
                    className={`w-6 h-6 rounded-full border-2 ${newMarker.color === color ? 'border-white' : 'border-transparent'}`}
                    style={{ backgroundColor: color }} />
                ))}
                <button onClick={() => setNewMarker({...newMarker, color: null})}
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${!newMarker.color ? 'border-white bg-[#334155]' : 'border-transparent bg-[#182136]'}`}>
                  <X className="w-3 h-3 text-[#94A3B8]" />
                </button>
              </div>
            </div>
            <div>
              <Label className="text-[#94A3B8]">Label (optional)</Label>
              <Input value={newMarker.label} onChange={e => setNewMarker({...newMarker, label: e.target.value})}
                className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" placeholder="e.g., Post Alpha, Exit B" />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Notes (optional)</Label>
              <Textarea value={newMarker.notes} onChange={e => setNewMarker({...newMarker, notes: e.target.value})}
                className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" rows={2} placeholder="Additional tactical notes..." />
            </div>
            {clickedPosition && (
              <p className="text-xs text-[#94A3B8]">
                Coordinates: {clickedPosition.lat.toFixed(6)}, {clickedPosition.lng.toFixed(6)}
              </p>
            )}
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1 border-[#334155] text-[#94A3B8]"
                onClick={() => { setShowAddMarker(false); setClickedPosition(null); }}>
                Cancel
              </Button>
              <Button className="flex-1 bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" onClick={handleAddMarker}>
                <Save className="w-4 h-4 mr-2" /> Place Marker
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Marker Dialog */}
      <Dialog open={showEditMarker} onOpenChange={setShowEditMarker}>
        <DialogContent className="bg-[#131A2A] border-[#334155] text-[#F8FAFC]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#F59E0B]" />
              Edit Marker
            </DialogTitle>
          </DialogHeader>
          {editingMarker && (
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-[#94A3B8]">Marker Type</Label>
                <Select value={editingMarker.marker_type} onValueChange={v => setEditingMarker({...editingMarker, marker_type: v})}>
                  <SelectTrigger className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#131A2A] border-[#334155]">
                    {Object.entries(MARKER_TYPES).map(([key, config]) => (
                      <SelectItem key={key} value={key} className="text-[#F8FAFC]">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
                          {config.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#94A3B8]">Custom Color</Label>
                <div className="flex gap-2 mt-1">
                  {COLOR_PALETTE.map(color => (
                    <button key={color} onClick={() => setEditingMarker({...editingMarker, color})}
                      className={`w-6 h-6 rounded-full border-2 ${editingMarker.color === color ? 'border-white' : 'border-transparent'}`}
                      style={{ backgroundColor: color }} />
                  ))}
                  <button onClick={() => setEditingMarker({...editingMarker, color: null})}
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${!editingMarker.color ? 'border-white bg-[#334155]' : 'border-transparent bg-[#182136]'}`}>
                    <X className="w-3 h-3 text-[#94A3B8]" />
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-[#94A3B8]">Label</Label>
                <Input value={editingMarker.label || ''} onChange={e => setEditingMarker({...editingMarker, label: e.target.value})}
                  className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" />
              </div>
              <div>
                <Label className="text-[#94A3B8]">Notes</Label>
                <Textarea value={editingMarker.notes || ''} onChange={e => setEditingMarker({...editingMarker, notes: e.target.value})}
                  className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" rows={2} />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1 border-[#334155] text-[#94A3B8]" onClick={() => setShowEditMarker(false)}>
                  Cancel
                </Button>
                <Button className="flex-1 bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" onClick={handleUpdateMarker}>
                  <Check className="w-4 h-4 mr-2" /> Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Shape Dialog */}
      <Dialog open={showAddShape} onOpenChange={(open) => { setShowAddShape(open); if (!open) setDrawnCoordinates([]); }}>
        <DialogContent className="bg-[#131A2A] border-[#334155] text-[#F8FAFC]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pentagon className="w-5 h-5 text-[#3B82F6]" />
              Save Drawing
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-[#94A3B8]">Shape Type</Label>
              <Select value={newShape.shape_type} onValueChange={v => setNewShape({...newShape, shape_type: v, color: SHAPE_TYPES[v]?.color || '#3B82F6'})}>
                <SelectTrigger className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#131A2A] border-[#334155]">
                  {Object.entries(SHAPE_TYPES).map(([key, config]) => (
                    <SelectItem key={key} value={key} className="text-[#F8FAFC]">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: config.color }} />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#94A3B8]">Color</Label>
              <div className="flex gap-2 mt-1">
                {COLOR_PALETTE.map(color => (
                  <button key={color} onClick={() => setNewShape({...newShape, color})}
                    className={`w-6 h-6 rounded border-2 ${newShape.color === color ? 'border-white' : 'border-transparent'}`}
                    style={{ backgroundColor: color }} />
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[#94A3B8]">Label</Label>
              <Input value={newShape.label} onChange={e => setNewShape({...newShape, label: e.target.value})}
                className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" placeholder="e.g., Primary Search Zone" />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Notes</Label>
              <Textarea value={newShape.notes} onChange={e => setNewShape({...newShape, notes: e.target.value})}
                className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" rows={2} placeholder="Additional notes..." />
            </div>
            <p className="text-xs text-[#94A3B8]">{drawnCoordinates.length} points drawn</p>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1 border-[#334155] text-[#94A3B8]"
                onClick={() => { setShowAddShape(false); setDrawnCoordinates([]); }}>
                Cancel
              </Button>
              <Button className="flex-1 bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" onClick={handleAddShape}>
                <Save className="w-4 h-4 mr-2" /> Save Shape
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Shape Dialog */}
      <Dialog open={showEditShape} onOpenChange={setShowEditShape}>
        <DialogContent className="bg-[#131A2A] border-[#334155] text-[#F8FAFC]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#F59E0B]" />
              Edit Shape
            </DialogTitle>
          </DialogHeader>
          {editingShape && (
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-[#94A3B8]">Type</Label>
                <p className="text-[#F8FAFC] text-sm">{SHAPE_TYPES[editingShape.shape_type]?.label || editingShape.shape_type}</p>
              </div>
              <div>
                <Label className="text-[#94A3B8]">Color</Label>
                <div className="flex gap-2 mt-1">
                  {COLOR_PALETTE.map(color => (
                    <button key={color} onClick={() => setEditingShape({...editingShape, color})}
                      className={`w-6 h-6 rounded border-2 ${editingShape.color === color ? 'border-white' : 'border-transparent'}`}
                      style={{ backgroundColor: color }} />
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-[#94A3B8]">Label</Label>
                <Input value={editingShape.label || ''} onChange={e => setEditingShape({...editingShape, label: e.target.value})}
                  className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" />
              </div>
              <div>
                <Label className="text-[#94A3B8]">Notes</Label>
                <Textarea value={editingShape.notes || ''} onChange={e => setEditingShape({...editingShape, notes: e.target.value})}
                  className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]" rows={2} />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1 border-[#334155] text-[#94A3B8]" onClick={() => setShowEditShape(false)}>
                  Cancel
                </Button>
                <Button className="flex-1 bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" onClick={handleUpdateShape}>
                  <Check className="w-4 h-4 mr-2" /> Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CaseOperationsMap;
